import { ConnectError, Code, type CallOptions } from "@connectrpc/connect";

import type { GitpodAPI } from "@/api";
import { AccountService } from "gitpod-next-api/gitpod/v1/account_pb";
import { EnvironmentAutomationService } from "gitpod-next-api/gitpod/v1/environment_automation_pb";
import { EnvironmentService } from "gitpod-next-api/gitpod/v1/environment_pb";
import { EventService } from "gitpod-next-api/gitpod/v1/event_pb";

export type RPCMessage = {
    type: "rpc";
    id: string;
    service: string;
    method: string;
    args: unknown[];
};

export type RPCCancelMessage = {
    type: "rpc-cancel";
    id: string;
};

export type RPCRequest = RPCMessage | RPCCancelMessage;

export type RPCResponse = {
    type: "rpc-response";
    id: string;
    result?: unknown;
    error?: RPCError;
    done?: boolean;
};

export type RPCError = {
    code: number;
    message: string;
    details?: unknown;
};

const activeStreams = new Map<string, AbortController>();

export async function handleRPCMessage(
    api: Record<string, unknown>,
    message: RPCRequest,
): Promise<AsyncIterable<RPCResponse> | RPCResponse | void> {
    if (message.type === "rpc-cancel") {
        activeStreams.get(message.id)?.abort();
        return;
    }

    try {
        const service = api[message.service];
        if (!service) {
            throw new ConnectError("Service not found", Code.Unimplemented);
        }

        const methodName = message.method;
        const methodImpl = Reflect.get(service, methodName) as unknown;
        if (typeof methodImpl !== "function") {
            throw new ConnectError("Method not found", Code.Unimplemented);
        }

        const controller = new AbortController();
        const options = (message.args[1] as CallOptions | undefined) || {};
        options.signal = controller.signal;
        message.args[1] = options;
        activeStreams.set(message.id, controller);

        const result = (await methodImpl.apply(service, message.args)) as unknown;

        if (Symbol.asyncIterator in Object(result)) {
            return handleStreamingResponse(message, result as AsyncIterable<unknown>, () => {
                activeStreams.delete(message.id);
            });
        }

        activeStreams.delete(message.id);
        return {
            type: "rpc-response",
            id: message.id,
            result,
        };
    } catch (err) {
        activeStreams.delete(message.id);
        return {
            type: "rpc-response",
            id: message.id,
            error: serializeError(err),
        };
    }
}

async function* handleStreamingResponse(
    message: RPCMessage,
    result: AsyncIterable<unknown>,
    onComplete: () => void,
): AsyncIterable<RPCResponse> {
    try {
        for await (const item of result) {
            yield {
                type: "rpc-response",
                id: message.id,
                result: item,
            };
        }
        yield {
            type: "rpc-response",
            id: message.id,
            done: true,
        };
    } catch (err) {
        yield {
            type: "rpc-response",
            id: message.id,
            error: serializeError(err),
        };
    } finally {
        onComplete();
    }
}

function serializeError(err: unknown): RPCError {
    if (err instanceof Error && err.name === "AbortError") {
        err = new ConnectError("Canceled", Code.Canceled);
    }
    if (err instanceof ConnectError) {
        return {
            code: err.code,
            message: err.message,
            details: err.details,
        };
    }
    return {
        code: Code.Internal,
        message: err instanceof Error ? err.message : String(err),
    };
}

export type IVSCodeIPC = {
    openFile(filePath: string): Promise<void>;
    resolve(environmentId: string, userId: string): Promise<void>;
    setVisibilityState(visible: boolean): Promise<void>;
    updateEnvironmentUrl(url: string): void;
    supportsEnvironmentUrlNavigation(): boolean;
    connectionId: string;
};

export class VSCodeIPC implements IVSCodeIPC {
    private channel: MessageChannel | undefined;
    private messageHandler: ((event: MessageEvent) => void) | undefined;
    private readonly exposedServices: Record<string, unknown>;

    // Promise that resolves when the message port is send to the vscode iframe.
    // Note that gitpod.requestMessagePort could happen more than once as the vscode iframe
    // will reload itself if the devcontainer.workspaceFolder gets resolved after resolveEditorURL is called
    private readonly messagePortPromise: Promise<void>;

    // Connection ID that changes each time a new MessageChannel is established
    // Used to trigger effects that need to run after each connection
    private _connectionId = "";
    get connectionId(): string {
        return this._connectionId;
    }

    private _supportsEnvironmentUrlNavigation = false;

    constructor(
        api: GitpodAPI,
        private readonly iframe: HTMLIFrameElement,
        private readonly theme: "light" | "dark",
        private environmentUrl: string,
    ) {
        this.exposedServices = {
            [AccountService.typeName]: api.accountService,
            [EnvironmentService.typeName]: api.environmentService,
            [EventService.typeName]: api.eventService,
            [EnvironmentAutomationService.typeName]: api.environmentAutomationService,
        };
        let resolve: () => void;
        this.messagePortPromise = new Promise<void>((r) => {
            resolve = r;
        });
        this.messageHandler = (event: MessageEvent) => {
            // Only accept messages from our iframe
            if (event.source !== this.iframe.contentWindow) {
                return;
            }

            if (event.data?.type === "gitpod.requestMessagePort") {
                if (event.data?.supportsEnvironmentUrlNavigation) {
                    this._supportsEnvironmentUrlNavigation = true;
                }

                this.channel?.port1.close();
                this.channel?.port2.close();
                this.channel = new MessageChannel();
                const { port1, port2 } = this.channel;

                // Keep port1 for handling messages
                this.setupMessageHandler(port1);

                // Send port2 to the iframe with theme and environmentUrl
                this.iframe.contentWindow?.postMessage(
                    {
                        type: "gitpod.messagePort",
                        theme: this.theme,
                        environmentUrl: this.environmentUrl,
                    },
                    "*",
                    [port2],
                );

                this._connectionId = crypto.randomUUID();

                resolve();
            }
        };

        window.addEventListener("message", this.messageHandler);
    }

    async openFile(filePath: string) {
        await this.messagePortPromise;
        this.channel?.port1.postMessage({
            type: "gitpod.agentExecution",
            action: "open-file",
            filePath,
        });
    }

    async resolve(environmentId: string, userId: string) {
        await this.messagePortPromise;
        this.channel?.port1.postMessage({
            type: "gitpod.agentExecution",
            action: "resolve",
            environmentId,
            userId,
        });
    }

    async setVisibilityState(visible: boolean) {
        await this.messagePortPromise;
        this.channel?.port1.postMessage({
            type: "gitpod.visibilityState",
            visible,
        });
    }

    updateEnvironmentUrl(url: string) {
        this.environmentUrl = url;
        this.iframe.contentWindow?.postMessage(
            {
                type: "gitpod.updateEnvironmentUrl",
                environmentUrl: url,
            },
            "*",
        );
    }

    supportsEnvironmentUrlNavigation(): boolean {
        return this._supportsEnvironmentUrlNavigation;
    }

    private setupMessageHandler(port: MessagePort) {
        port.onmessage = async (event) => {
            // Handle non-RPC messages from the VS Code extension
            if (event.data?.type === "gitpod.focusConversationPrompt") {
                document.dispatchEvent(new CustomEvent("gitpod:focusConversationPrompt"));
                return;
            }

            const message = event.data as RPCMessage;
            if (message.type !== "rpc") {
                return;
            }

            try {
                const responseOrStream = await handleRPCMessage(this.exposedServices, message);
                if (responseOrStream === undefined) {
                    return;
                }

                if (Symbol.asyncIterator in Object(responseOrStream)) {
                    for await (const response of responseOrStream as AsyncIterable<RPCResponse>) {
                        port.postMessage(response);
                    }
                } else {
                    port.postMessage(responseOrStream as RPCResponse);
                }
            } catch (err) {
                console.error("Error handling RPC message:", err);
            }
        };

        port.start();
    }

    dispose() {
        if (this.messageHandler) {
            window.removeEventListener("message", this.messageHandler);
            this.messageHandler = undefined;
        }
        this.channel?.port1.close();
        this.channel?.port2.close();
    }
}
